import {
  EXPENSE_CATEGORY_LABELS,
  Expense as ExpenseSchema,
  ExpenseCategory,
  type PurchasePayment,
  type StoredFile,
  addMonthsIso,
  formatBRL,
  formatIsoDate,
  isoDateInSaoPaulo,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { AffixInput, Field, SwitchRow } from '../../components/Form';
import { Alert } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, newIdempotencyKey } from '../../lib/api';
import { useInvalidateExpenses } from '../../lib/expenses-api';
import { uploadFile } from '../../lib/files-api';
import { errorMessage } from '../../lib/messages';
import { parseBrDate, parseCentavos } from '../../lib/numbers';
import { useIsPhone } from '../../lib/use-media';
import { ExpensesPage } from './ExpensesPage';
import { EquipmentFields, PaymentSelect } from './dialogs';

/** `/despesas/nova`: the phone form (board Despesa-Celular); desktop opens the dialog. */
export function NewExpenseRoute() {
  const phone = useIsPhone();
  return phone ? <NewExpensePhone /> : <ExpensesPage startNew />;
}

const PHONE_PAYMENTS: PurchasePayment[] = ['credit_card', 'pix', 'boleto', 'cash', 'debit_card'];

function NewExpensePhone() {
  const toast = useToast();
  const invalidate = useInvalidateExpenses();
  const today = isoDateInSaoPaulo();
  const photo = useRef<HTMLInputElement>(null);
  const [receipt, setReceipt] = useState<StoredFile | null>(null);
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ExpenseCategory | null>(null);
  const [date, setDate] = useState(formatIsoDate(today));
  const [payment, setPayment] = useState<PurchasePayment>('credit_card');
  const [supplier, setSupplier] = useState('');
  const [isEquipment, setIsEquipment] = useState(false);
  const [life, setLife] = useState('24');
  const [count, setCount] = useState(1);
  const [firstDue, setFirstDue] = useState(formatIsoDate(addMonthsIso(today, 1)));
  const [tried, setTried] = useState(false);
  const [idem, setIdem] = useState(newIdempotencyKey);
  const [saved, setSaved] = useState<string | null>(null);
  const valueC = parseCentavos(value);
  const iso = parseBrDate(date);
  const cat: ExpenseCategory | null = isEquipment ? 'equipment' : category;
  const okValue = !!valueC && valueC > 0;
  const upload = useMutation({
    mutationFn: (f: File) => uploadFile(f, 'document'),
    onSuccess: (f) => {
      if (receipt) toast('Foto do recibo trocada');
      setReceipt(f);
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const save = useMutation({
    mutationFn: () =>
      api.post(
        '/expenses',
        ExpenseSchema,
        {
          spentOn: iso,
          description: description.trim() || EXPENSE_CATEGORY_LABELS[cat!],
          category: cat,
          amountCentavos: valueC,
          paymentMethod: payment,
          supplier: supplier.trim() || null,
          fileIds: receipt ? [receipt.id] : [],
          equipment: isEquipment
            ? {
                lifeMonths: Number(life),
                installments:
                  count > 1 && parseBrDate(firstDue)
                    ? { count, firstDueOn: parseBrDate(firstDue) }
                    : null,
              }
            : null,
        },
        { idempotencyKey: idem },
      ),
    onSuccess: (e) => {
      invalidate();
      setSaved(
        `${e.description} · ${formatBRL(e.amountCentavos ?? 0)} · ${
          isEquipment && valueC
            ? `equipamento, ${formatBRL(Math.round(valueC / Number(life)))} por mês`
            : EXPENSE_CATEGORY_LABELS[e.category]
        }`,
      );
    },
  });
  const reset = () => {
    setReceipt(null);
    setValue('');
    setDescription('');
    setCategory(null);
    setIsEquipment(false);
    setTried(false);
    setIdem(newIdempotencyKey());
    setSaved(null);
  };

  if (saved)
    return (
      <div className="card col" style={{ gap: 12, borderColor: 'var(--ok)' }}>
        <span className="badge ok" style={{ alignSelf: 'flex-start' }}>
          Despesa salva
        </span>
        <p>{saved}</p>
        <Link className="btn" to="/despesas">
          Ver em Despesas
        </Link>
        <button type="button" className="btn p" onClick={reset}>
          Lançar outra
        </button>
      </div>
    );

  return (
    <div className="col" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 8 }}>
        <Link className="btn icon g" to="/despesas" aria-label="Voltar para Despesas">
          ‹
        </Link>
        <h1 style={{ fontSize: 22 }}>Nova despesa</h1>
      </div>
      <input
        ref={photo}
        type="file"
        hidden
        accept="image/*,.pdf"
        capture="environment"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
          e.target.value = '';
        }}
      />
      {receipt ? (
        <div className="card row" style={{ gap: 12 }}>
          {receipt.mime.startsWith('image/') ? (
            <img
              src={receipt.url}
              alt="Foto do recibo"
              style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 12 }}
            />
          ) : (
            <span className="badge">PDF</span>
          )}
          <div className="grow col" style={{ gap: 4 }}>
            <span className="badge ok" style={{ alignSelf: 'flex-start' }}>
              Recibo anexado
            </span>
            <span className="xs muted">{receipt.name}</span>
          </div>
          <button
            type="button"
            className="btn sm"
            disabled={upload.isPending}
            onClick={() => photo.current?.click()}
          >
            Tirar outra
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="dropzone"
          style={{ cursor: 'pointer', font: 'inherit' }}
          disabled={upload.isPending}
          onClick={() => photo.current?.click()}
        >
          <span style={{ fontWeight: 600 }}>
            {upload.isPending ? 'Enviando…' : 'Fotografar o recibo'}
          </span>
          <span className="xs muted">ou escolher um PDF · opcional</span>
        </button>
      )}
      <Field
        id="nx-value"
        label="Valor"
        error={tried && !okValue ? 'Informe um valor maior que zero.' : null}
      >
        <AffixInput
          id="nx-value"
          prefix="R$"
          placeholder="0,00"
          value={value}
          onChange={setValue}
          invalid={tried && !okValue}
        />
      </Field>
      <Field id="nx-desc" label="Descrição">
        <input
          id="nx-desc"
          className="input"
          placeholder="Ex.: Secador de filamento"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field label="Categoria" error={tried && !cat ? 'Escolha uma categoria.' : null}>
        <div className="row wrap" role="radiogroup" aria-label="Categoria" style={{ gap: 6 }}>
          {ExpenseCategory.options.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={cat === c}
              className={cat === c ? 'chip on' : 'chip'}
              disabled={isEquipment && c !== 'equipment'}
              onClick={() => setCategory(category === c ? null : c)}
            >
              {EXPENSE_CATEGORY_LABELS[c]}
            </button>
          ))}
        </div>
      </Field>
      <div className="row" style={{ gap: 12 }}>
        <Field
          id="nx-date"
          label="Data"
          style={{ flex: 1 }}
          error={tried && !iso ? 'Use DD/MM/AAAA.' : null}
        >
          <input
            id="nx-date"
            className="input tab"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field id="nx-pay" label="Pagamento" style={{ flex: 1 }}>
          <PaymentSelect
            id="nx-pay"
            value={payment}
            onChange={setPayment}
            options={PHONE_PAYMENTS}
          />
        </Field>
      </div>
      <Field id="nx-supplier" label="Fornecedor">
        <input
          id="nx-supplier"
          className="input"
          placeholder="opcional"
          value={supplier}
          onChange={(e) => setSupplier(e.target.value)}
        />
      </Field>
      <div className="card col" style={{ gap: 10 }}>
        <SwitchRow
          on={isEquipment}
          onChange={(v) => {
            setIsEquipment(v);
            if (!v && category === 'equipment') setCategory(null);
          }}
          label="Lançar como equipamento"
        />
        <p className="xs muted">
          Vira um ativo e é diluído na vida útil, em vez de pesar só neste mês.
        </p>
        {isEquipment ? (
          <EquipmentFields
            valueC={valueC}
            life={life}
            setLife={setLife}
            count={count}
            setCount={setCount}
            firstDue={firstDue}
            setFirstDue={setFirstDue}
          />
        ) : null}
      </div>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
      <button
        type="button"
        className="btn p block"
        disabled={save.isPending}
        onClick={() => {
          setTried(true);
          if (!okValue) toast('Confira o valor');
          else if (!cat) toast('Escolha uma categoria');
          else if (iso && (!isEquipment || Number(life) >= 1)) save.mutate();
        }}
      >
        Salvar despesa
      </button>
    </div>
  );
}
